import React, { useState, useEffect } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonGrid,
  IonRow,
  IonCol,
  IonIcon,
  IonSpinner,
  IonChip,
  IonList,
  IonItem,
  IonLabel,
  IonButton,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent,
  IonText,
  IonBadge,
} from '@ionic/react';
import {
  walletOutline,
  ticketOutline,
  trendingUpOutline,
  trendingDownOutline,
  listOutline,
  statsChartOutline,
  closeOutline,
  layersOutline,
  cashOutline,
  timeOutline,
  businessOutline,
} from 'ionicons/icons';
import { adminApiService, UserInventory, UserTickets } from '../services/adminApi';
import { format } from 'date-fns';
import { getCurrencySymbol, DEFAULT_CURRENCY } from '../utils/currencyConstants';

interface AdminUserInventoryTabProps {
  userId: string;
}

const AdminUserInventoryTab: React.FC<AdminUserInventoryTabProps> = ({ userId }) => {
  const [inventory, setInventory] = useState<UserInventory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTicketsModal, setShowTicketsModal] = useState(false);
  const [userTickets, setUserTickets] = useState<UserTickets | null>(null);
  const [loadingTickets, setLoadingTickets] = useState(false);

  useEffect(() => {
    loadInventory();
  }, [userId]);

  const loadInventory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApiService.getUserInventory(userId);
      setInventory(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load inventory data');
      console.error('Error loading inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUserTickets = async () => {
    try {
      setLoadingTickets(true);
      const data = await adminApiService.getUserTickets(userId);
      setUserTickets(data);
    } catch (err: any) {
      console.error('Error loading user tickets:', err);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleShowTickets = () => {
    setShowTicketsModal(true);
    if (!userTickets) {
      loadUserTickets();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'sold':
        return 'medium';
      case 'reserved':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const getSourceTypeLabel = (sourceType?: string) => {
    switch (sourceType) {
      case 'supplier':
        return 'Supplier';
      case 'site':
        return 'Platform';
      case 'private':
        return 'Private';
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <IonSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <IonText color="danger">
          <p>{error}</p>
        </IonText>
        <IonButton fill="outline" onClick={loadInventory} className="mt-4">
          Retry
        </IonButton>
      </div>
    );
  }

  if (!inventory) {
    return (
      <div className="text-center py-12">
        <IonText color="medium">
          <p>No inventory data available</p>
        </IonText>
      </div>
    );
  }

  return (
    <>
      {/* Inventory Overview Cards */}
      <IonGrid className="p-0">
        <IonRow>
          <IonCol size="12" sizeMd="6">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle className="flex items-center text-lg">
                  <IonIcon icon={walletOutline} className="mr-2 text-primary" />
                  Inventory Value
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">
                    {getCurrencySymbol(DEFAULT_CURRENCY)}{inventory.totalInventoryValue.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">Total Investment</p>
                </div>
              </IonCardContent>
            </IonCard>
          </IonCol>

          <IonCol size="12" sizeMd="6">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle className="flex items-center text-lg">
                  <IonIcon icon={trendingUpOutline} className="mr-2 text-success" />
                  Profit & Revenue
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Revenue:</span>
                    <span className="font-semibold text-success">
                      {getCurrencySymbol(DEFAULT_CURRENCY)}{inventory.totalRevenue.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Profit:</span>
                    <span className={`font-semibold ${inventory.totalProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                      {getCurrencySymbol(DEFAULT_CURRENCY)}{inventory.totalProfit.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Margin:</span>
                    <span className={`font-semibold ${inventory.profitMargin >= 0 ? 'text-success' : 'text-danger'}`}>
                      {inventory.profitMargin.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>

        <IonRow>
          <IonCol size="12" sizeMd="4">
            <IonCard>
              <IonCardContent className="text-center">
                <IonIcon icon={ticketOutline} className="text-4xl text-primary mb-2" />
                <p className="text-2xl font-bold">{inventory.availableTickets}</p>
                <p className="text-sm text-gray-500">Available</p>
              </IonCardContent>
            </IonCard>
          </IonCol>

          <IonCol size="12" sizeMd="4">
            <IonCard>
              <IonCardContent className="text-center">
                <IonIcon icon={cashOutline} className="text-4xl text-success mb-2" />
                <p className="text-2xl font-bold">{inventory.soldTickets}</p>
                <p className="text-sm text-gray-500">Sold</p>
              </IonCardContent>
            </IonCard>
          </IonCol>

          <IonCol size="12" sizeMd="4">
            <IonCard>
              <IonCardContent className="text-center">
                <IonIcon icon={timeOutline} className="text-4xl text-warning mb-2" />
                <p className="text-2xl font-bold">{inventory.reservedTickets}</p>
                <p className="text-sm text-gray-500">Reserved</p>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>
      </IonGrid>

      {/* Summary Statistics */}
      <IonCard className="mt-4">
        <IonCardHeader>
          <IonCardTitle className="flex items-center">
            <IonIcon icon={statsChartOutline} className="mr-2" />
            Summary Statistics
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonGrid className="p-0">
            <IonRow>
              <IonCol size="6">
                <div className="text-center">
                  <p className="text-lg font-bold">{inventory.summary.totalTickets}</p>
                  <p className="text-sm text-gray-500">Total Tickets</p>
                </div>
              </IonCol>
              <IonCol size="6">
                <div className="text-center">
                  <p className="text-lg font-bold">{inventory.summary.totalSales}</p>
                  <p className="text-sm text-gray-500">Total Sales</p>
                </div>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="6">
                <div className="text-center">
                  <p className="text-lg font-bold">
                    {getCurrencySymbol(DEFAULT_CURRENCY)}{inventory.summary.averageTicketPrice.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-500">Avg. Ticket Price</p>
                </div>
              </IonCol>
              <IonCol size="6">
                <div className="text-center">
                  <p className="text-lg font-bold">{inventory.summary.turnoverRate.toFixed(1)}%</p>
                  <p className="text-sm text-gray-500">Turnover Rate</p>
                </div>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonCardContent>
      </IonCard>

      {/* Top Categories */}
      {inventory.topCategories.length > 0 && (
        <IonCard className="mt-4">
          <IonCardHeader>
            <IonCardTitle className="flex items-center">
              <IonIcon icon={layersOutline} className="mr-2" />
              Top Categories
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <div className="space-y-2">
              {inventory.topCategories.map((category, index) => (
                <div key={index} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded">
                  <div>
                    <p className="font-semibold">{category.category}</p>
                    <p className="text-sm text-gray-500">{category.count} tickets</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">
                      {getCurrencySymbol(DEFAULT_CURRENCY)}{category.value.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </IonCardContent>
        </IonCard>
      )}

      {/* Recent Tickets */}
      {inventory.recentTickets.length > 0 && (
        <IonCard className="mt-4">
          <IonCardHeader>
            <IonCardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <IonIcon icon={listOutline} className="mr-2" />
                Recent Tickets
              </div>
              <IonButton fill="outline" size="small" onClick={handleShowTickets}>
                View All
              </IonButton>
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList className="p-0">
              {inventory.recentTickets.slice(0, 5).map((ticket) => (
                <IonItem key={ticket._id} className="mb-2">
                  <IonLabel>
                    <h3 className="font-semibold">{ticket.eventId?.name || ticket.eventName}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      {ticket.category && (
                        <IonChip color="primary" className="text-xs">
                          {ticket.category}
                        </IonChip>
                      )}
                      {ticket.section && (
                        <IonChip color="secondary" className="text-xs">
                          {ticket.section}
                        </IonChip>
                      )}
                      <IonBadge color={getStatusColor(ticket.status)}>
                        {ticket.status}
                      </IonBadge>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-gray-500">
                        Qty: {ticket.quantity} × {getCurrencySymbol(DEFAULT_CURRENCY)}{ticket.purchasePrice}
                      </span>
                      <span className="text-sm text-gray-500">
                        {format(new Date(ticket.createdAt), 'MMM dd, yyyy')}
                      </span>
                    </div>
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          </IonCardContent>
        </IonCard>
      )}

      {/* All Tickets Modal */}
      <IonModal isOpen={showTicketsModal} onDidDismiss={() => setShowTicketsModal(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>All User Tickets</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowTicketsModal(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {loadingTickets ? (
            <div className="flex justify-center items-center py-12">
              <IonSpinner />
            </div>
          ) : userTickets ? (
            <div className="p-4">
              <div className="mb-4">
                <IonText>
                  <h2 className="text-lg font-semibold">
                    Total Tickets: {userTickets.total}
                  </h2>
                </IonText>
              </div>
              
              <IonList>
                {userTickets.tickets.map((ticket) => (
                  <IonCard key={ticket._id} className="mb-3">
                    <IonCardContent>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">
                            {ticket.eventId?.name || ticket.eventName}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {ticket.eventId?.venue || ticket.venue} • {' '}
                            {format(new Date(ticket.eventId?.date || ticket.eventDate), 'MMM dd, yyyy')}
                          </p>
                        </div>
                        <IonBadge color={getStatusColor(ticket.status)}>
                          {ticket.status}
                        </IonBadge>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-3">
                        {ticket.category && (
                          <IonChip color="primary" className="text-xs">
                            {ticket.category}
                          </IonChip>
                        )}
                        {ticket.section && (
                          <IonChip color="secondary" className="text-xs">
                            Section: {ticket.section}
                          </IonChip>
                        )}
                        {ticket.block && (
                          <IonChip color="tertiary" className="text-xs">
                            Block: {ticket.block}
                          </IonChip>
                        )}
                        {ticket.sourceType && (
                          <IonChip color="medium" className="text-xs">
                            <IonIcon icon={businessOutline} className="mr-1" />
                            {getSourceTypeLabel(ticket.sourceType)}
                          </IonChip>
                        )}
                      </div>

                      <IonGrid className="p-0">
                        <IonRow>
                          <IonCol size="6">
                            <p className="text-sm">
                              <strong>Quantity:</strong> {ticket.quantity}
                              {ticket.soldQuantity ? ` (${ticket.soldQuantity} sold)` : ''}
                            </p>
                          </IonCol>
                          <IonCol size="6">
                            <p className="text-sm">
                              <strong>Purchase Price:</strong> {getCurrencySymbol(DEFAULT_CURRENCY)}{ticket.purchasePrice}
                            </p>
                          </IonCol>
                        </IonRow>
                        {ticket.wantedPrice && (
                          <IonRow>
                            <IonCol size="6">
                              <p className="text-sm">
                                <strong>Wanted Price:</strong> {getCurrencySymbol(DEFAULT_CURRENCY)}{ticket.wantedPrice}
                              </p>
                            </IonCol>
                            <IonCol size="6">
                              <p className="text-sm text-gray-500">
                                Added: {format(new Date(ticket.createdAt), 'MMM dd, yyyy')}
                              </p>
                            </IonCol>
                          </IonRow>
                        )}
                      </IonGrid>

                      {ticket.supplierId && (
                        <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <p className="text-sm">
                            <strong>Supplier:</strong> {ticket.supplierId.name}
                            {ticket.supplierId.email && ` (${ticket.supplierId.email})`}
                          </p>
                        </div>
                      )}
                    </IonCardContent>
                  </IonCard>
                ))}
              </IonList>
            </div>
          ) : (
            <div className="text-center py-12">
              <IonText color="medium">
                <p>Failed to load tickets</p>
              </IonText>
            </div>
          )}
        </IonContent>
      </IonModal>
    </>
  );
};

export default AdminUserInventoryTab;